import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, LinearProgress, Stack, Typography } from "@mui/material";
import orderBy from "lodash/orderBy";
import { useSnackbar } from "notistack";
import type { FC } from "react";
import { useState } from "react";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import { Select } from "../../components/select/Select";
import dayjs, { DATE_FORMAT, ISO_DATE_FORMAT } from "../../dayjs";
import type { CustomerContactPerson } from "../customersApi";
import type { LoyaltyPointEvent } from "./LoyaltyPointsList";
import { LoyaltyPointsList } from "./LoyaltyPointsList";
import { useAdminLoyaltyPointsHistory } from "./useAdminLoyaltyPoints";
import { useRefundLoyaltyPoints } from "./useRefundLoyaltyPoints";

interface Props {
  readonly contacts: Array<CustomerContactPerson>;
  readonly readonly?: boolean;
}

const LoyaltyPointsContent: FC<Props> = ({ contacts, readonly }) => {
  const { enqueueSnackbar } = useSnackbar();
  const contactIds = contacts.map((c) => c.loginEmail).filter((email): email is string => !!email);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { loyaltyPointsHistory, isLoading, hasNext } = useAdminLoyaltyPointsHistory(contactIds, page, rowsPerPage);

  const { refundLoyaltyPoints } = useRefundLoyaltyPoints();
  const [contact, setContact] = useState<string>("");
  const [confirmationDialog, setConfirmationDialog] = useState<{
    content: string;
    onConfirm: () => Promise<void>;
  }>();

  const filtered = loyaltyPointsHistory.filter((e) => contact === "" || e.userId == contact);
  const orderedLoyaltyPointsHistory = orderBy(filtered, (h) => h.createdAt, ["desc"]);

  const handleRefundPoints = async (event: LoyaltyPointEvent) => {
    if (event.type != "LOYALTY_POINTS_SPENT") {
      enqueueSnackbar(`Tylko wydane punkty mogą być refundowane`, { variant: "error" });
      return;
    }
    setConfirmationDialog({
      content: `Refundować ${-event.amount} pkt za nagrode ${event.rewardId} z ${event.createdAt.format(DATE_FORMAT)}?`,
      onConfirm: async () => {
        await refundLoyaltyPoints({ eventId: event.id });
        setConfirmationDialog(undefined);
      },
    });
  };

  const count = page * rowsPerPage + orderedLoyaltyPointsHistory.length + (hasNext ? 1 : 0);

  return (
    <>
      {isLoading && <LinearProgress data-testid="loading-indicator" />}
      <Stack
        sx={{
          marginTop: "16px",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: "space-between",
          }}
        >
          <Select
            label="Osoba kontaktowa"
            style={{ flexGrow: 1 }}
            value={contact}
            values={[
              { label: " ", value: "" },
              ...contacts.flatMap((c) => (c.loginEmail ? [{ label: c.name ?? "", value: c.loginEmail }] : [])),
            ]}
            onSelect={(c) => setContact(c)}
          />
        </Stack>
        <LoyaltyPointsList
          values={orderedLoyaltyPointsHistory.map((h) => ({ ...h, createdAt: dayjs(h.createdAt, ISO_DATE_FORMAT) }))}
          onRefund={handleRefundPoints}
          readonly={readonly}
          page={page}
          rowsPerPage={rowsPerPage}
          count={count}
          onPageChange={setPage}
          onRowsPerPageChange={(newSize) => {
            setRowsPerPage(newSize);
            setPage(0);
          }}
        />
        <ConfirmationDialog
          open={!!confirmationDialog}
          title="Refundacja punktów"
          content={confirmationDialog?.content ?? ""}
          onCancel={() => setConfirmationDialog(undefined)}
          onConfirm={async () => {
            await confirmationDialog?.onConfirm();
          }}
        />
      </Stack>
    </>
  );
};

const LoyaltyPoints: FC<Props> = ({ contacts, readonly }) => {
  return (
    <Accordion slotProps={{ transition: { unmountOnExit: true, mountOnEnter: true } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Historia punktów lojalnosciowych</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <LoyaltyPointsContent contacts={contacts} readonly={readonly} />
      </AccordionDetails>
    </Accordion>
  );
};

export default LoyaltyPoints;
